import { FormikValues, setNestedObjectValues, useFormik } from "formik";
import { useAppAttributes } from "../useAppAttributes";
import { useCallback, useEffect, useState } from "react";
import { CommunicationType } from "../../enums/CommunicationType";
import { dispatchCustomEvent } from "../../services/widgetEventsService";
import { WidgetDispatchEvents, WidgetListeningEvents } from "../../enums/WidgetEvents";
import { useDebouncedCallback } from "use-debounce";

const DISPATCH_EVENT_TIMEOUT_MS = 50;

interface Params<T extends FormikValues> {
  form: ReturnType<typeof useFormik<T>>;
}

export const usePackageSearchFormEvents = <T extends FormikValues>({ form }: Params<T>) => {
  const appAttributes = useAppAttributes();
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (appAttributes.communicationType === CommunicationType.Event && !isInitialized) {
      dispatchCustomEvent({ widgetEvent: WidgetDispatchEvents.PackageReadyForValues });
    }
  }, [isInitialized, appAttributes.communicationType]);

  // Listeners
  const setValuesFromEvent = useCallback(
    async ({ detail }: CustomEvent<{ values: any; touched?: any }>) => {
      form.setValues(detail.values);
      if (detail.touched) {
        form.setTouched(detail.touched);
      }
      if (!isInitialized) {
        const errors = await form.validateForm(detail.values);
        form.setTouched(setNestedObjectValues(errors, true));
        setIsInitialized(true);
      }
    },
    [form, isInitialized]
  );
  useEffect(() => {
    if (appAttributes.communicationType !== CommunicationType.Event) return;
    window.addEventListener<any>(WidgetListeningEvents.SetPackageValues, setValuesFromEvent);
    return () => {
      window.removeEventListener<any>(WidgetListeningEvents.SetPackageValues, setValuesFromEvent);
    };
  }, [appAttributes.communicationType, form, setValuesFromEvent]);

  // Dispatchers
  const debouncedDispatchCustomEvent = useDebouncedCallback(
    dispatchCustomEvent,
    DISPATCH_EVENT_TIMEOUT_MS
  );
  useEffect(() => {
    if (appAttributes.communicationType !== CommunicationType.Event || !isInitialized) return;
    debouncedDispatchCustomEvent({
      widgetEvent: WidgetDispatchEvents.PackageValuesChanged,
      details: { values: form.values, errors: form.errors, touched: form.touched },
    });
  }, [
    appAttributes.communicationType,
    debouncedDispatchCustomEvent,
    form.errors,
    form.touched,
    form.values,
    isInitialized,
  ]);
};
